import { useCallback, useState } from 'react'
import { arrayMove } from '@dnd-kit/sortable'
import { funnelApi } from '../api'
import type { FunnelStep, CreateStepPayload } from '../types'

export function useFunnelSteps(
  funnelId: string | undefined,
  steps: FunnelStep[],
  setSteps: (steps: FunnelStep[]) => void,
  refetch: () => Promise<void>
) {
  const [savingStepIds, setSavingStepIds] = useState<Set<string>>(new Set())
  const createStep = useCallback(async (data: CreateStepPayload) => {
    if (!funnelId) return
    await funnelApi.createStep(funnelId, data)
    await refetch()
  }, [funnelId, refetch])

  const deleteStep = useCallback(async (stepId: string) => {
    if (!funnelId) return
    await funnelApi.deleteStep(funnelId, stepId)
    await refetch()
  }, [funnelId, refetch])

  const reorderSteps = useCallback(async (activeId: string, overId: string) => {
    if (!funnelId || activeId === overId) return

    const oldIndex = steps.findIndex((s) => s.id === activeId)
    const newIndex = steps.findIndex((s) => s.id === overId)
    if (oldIndex < 0 || newIndex < 0) return

    // Store original state for rollback
    const originalSteps = [...steps]

    // Optimistically update UI
    const reordered = arrayMove(steps, oldIndex, newIndex)
    const updated = reordered.map((step, i) => ({ ...step, position: i }))
    setSteps(updated)

    try {
      // Only update steps whose positions changed
      const minIndex = Math.min(oldIndex, newIndex)
      const maxIndex = Math.max(oldIndex, newIndex)
      const affectedSteps = updated.slice(minIndex, maxIndex + 1)

      // Mark affected steps as saving
      setSavingStepIds(new Set(affectedSteps.map((s) => s.id)))

      await Promise.all(
        affectedSteps.map((step) => funnelApi.reorderStep(funnelId, step.id, step.position))
      )

      // Clear saving state
      setSavingStepIds(new Set())
    } catch (error) {
      // Clear saving state and rollback UI on error
      setSavingStepIds(new Set())
      console.error('Failed to reorder steps:', error)
      setSteps(originalSteps)
      // TODO: Add toast notification when toast library is available
      // toast.error('Failed to reorder steps. Please try again.')

      // Optionally refetch to ensure sync with backend
      await refetch()
    }
  }, [funnelId, steps, setSteps, refetch])

  const updateStep = useCallback(async (stepId: string, data: Partial<FunnelStep>) => {
    if (!funnelId) return
    await funnelApi.updateStep(funnelId, stepId, data)
    await refetch()
  }, [funnelId, refetch])

  return {
    createStep,
    deleteStep,
    reorderSteps,
    updateStep,
    savingStepIds,
  }
}
